import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Controller,
  ResolverOptions,
  ResolverResult,
  useForm,
} from 'react-hook-form';
import { useFetcher, useLocation, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { parse, isValid, isAfter, startOfDay } from 'date-fns';
import { z } from 'zod';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { ResponsiveFormRow } from '@ldt/features/orders';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { NccnInfoTooltip } from '@ldt/features/orders/components/nccn_info_tooltip';
import { LdtOrderFormHeader } from '@ldt/features/orders/components/order_form_header';
import { OrderingProviderFormSection } from '@ldt/features/orders/components/ordering_provider_form_section';
import {
  PatientInformationFormSchema,
  PatientInformationFormSection,
} from '@ldt/features/orders/components/patient_information_form_section';
import { ProstateBiopsyClinicalInformationFormData } from '@ldt/features/orders/components/prostate_biopsy_clinical_information_form_section';
import { useOrderingProviderFormSection } from '@ldt/features/orders/hooks/use_ordering_provider_form_section';
import { OrderingProviderForm } from '@ldt/features/orders/ordering_provider_form';
import InfoIcon from 'src/assets/info_icon.svg';
import { FormDatePicker } from 'src/components/date_picker';
import { DecimalInput } from 'src/components/decimal_input';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Caution } from 'src/components/icons/caution';
import { IntegerInput } from 'src/components/integer_input';
import { RadioGroupField } from 'src/components/radio_group';
import { SectionHeader } from 'src/components/section_header';
import { SelectList } from 'src/components/select_list';
import { GleasonScoreFields } from 'src/distribution_platforms/features/components/gleason_score_form_fields';
import {
  processFetcherResponse,
  getProstateBiopsyTStageOptions,
  safeEmptyToUndefined,
  safeParseInt,
} from 'src/distribution_platforms/features/order_form';
import { hasSemanticFormChanges } from 'src/distribution_platforms/features/utils/patient_utils';
import { useDebounce } from 'src/hooks/use_debounce';
import {
  Ethnicity,
  NccnRiskCategory,
  LdtOrderForReviseReportFieldsFragment,
  ReviseReportInput,
  SpecimenType,
  TStage,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import {
  NccnRiskCategoryUtils,
  notifyError,
  notifySuccess,
  SpecimenTypeUtils,
} from 'src/utils';

const FORM_CONSTANTS = {
  WIDTH: '100%',
  MIN_AGE: 18,
  MAX_AGE: 100,
  PSA_MIN: 0,
} as const;

/** Field keys excluded from "must revise at least 1 field" check (e.g. revision reason is required and will always be dirty to submit) */
const FIELDS_EXCLUDED_FROM_REVISE_ONE_CHECK = new Set<string>([
  'revisionReason',
  'totalCores',
  'positiveCores',
]);

// Type for form data - inferred from the schema created with default values
type LdtProstateBiopsyReviseReportFormData = z.infer<
  ReturnType<typeof createLdtProstateBiopsyReviseReportFormSchema>
>;

const NCCN_RISK_CATEGORY_OPTIONS = [
  NccnRiskCategory.VeryLow,
  NccnRiskCategory.Low,
  NccnRiskCategory.IntermediateFavorable,
  NccnRiskCategory.IntermediateUnfavorable,
  NccnRiskCategory.High,
  NccnRiskCategory.VeryHigh,
].map((category) => ({
  value: category,
  name: NccnRiskCategoryUtils.getNccnRiskCategoryName(category),
}));

// Revise report is only available to CS/Sales;
const createLdtProstateBiopsyReviseReportFormSchema = () =>
  z.object({
    // Patient Information
    ...PatientInformationFormSchema.shape,

    // Ordering Provider
    orderingProviderId: z.string().min(1, 'Ordering Provider is required'),

    // Clinical Information
    ...ProstateBiopsyClinicalInformationFormData(true, FORM_CONSTANTS.PSA_MIN)
      .shape,

    dateOfBiopsy: z
      .string()
      .min(
        1,
        `${SpecimenTypeUtils.getSpecimenTypeInputLabel(SpecimenType.ProstateBiopsy)} is required`,
      )
      .refine(
        (val) => {
          if (!val || !val.trim()) return false;
          try {
            const date = parse(val.trim(), 'yyyy-MM-dd', new Date());
            if (!isValid(date)) return false;
            return !isAfter(date, startOfDay(new Date()));
          } catch {
            return false;
          }
        },
        {
          message: `${SpecimenTypeUtils.getSpecimenTypeInputLabel(
            SpecimenType.ProstateBiopsy,
          )} cannot be a future date`,
        },
      ),

    // Specimen ID - required, allowed: alphanumeric, dash, underscore, dot, comma, parentheses, colon, semicolon, space, slash
    specimenId: z
      .string()
      .min(1, 'Specimen ID is required')
      .regex(
        /^[a-zA-Z0-9\-_.(),:; /]+$/,
        'Allowed characters: alphanumeric, dash, underscore, dot, comma, parentheses, colon, semicolon, space, slash',
      ),

    // Revision Reason - required, always default ''
    revisionReason: z
      .string()
      .min(1, 'Revision Reason is required')
      .max(400, 'Revision Reason must be at most 400 characters'),
  });

// Custom resolver - only handles blocking errors for CS users
const createCustomResolver =
  (warningsRef?: {
    current: Partial<
      Record<keyof LdtProstateBiopsyReviseReportFormData, string>
    >;
  }) =>
  async (
    data: LdtProstateBiopsyReviseReportFormData,
    context: unknown,
    options: ResolverOptions<LdtProstateBiopsyReviseReportFormData>,
  ) => {
    const schema = createLdtProstateBiopsyReviseReportFormSchema();
    const zodResult = await zodResolver(schema)(data, context, options);

    // Manual cross-field validation (blocking errors only)
    const customErrors = { ...zodResult.errors };

    // Compute warnings (non-blocking) - these will be returned from the resolver
    const warnings: Partial<
      Record<keyof LdtProstateBiopsyReviseReportFormData, string>
    > = {};

    // NCCN warning for all users
    const nccnWarning = computeNccnWarning(
      data.nccnRiskCategory,
      data.primaryGleasonScore,
      data.secondaryGleasonScore,
      data.baselinePsa ?? undefined,
      data.tStage,
    );
    if (nccnWarning) {
      warnings.nccnRiskCategory = nccnWarning;
    }

    // PSA warning
    const psaWarning = computePsaWarning(data.baselinePsa ?? undefined);
    if (psaWarning) {
      warnings.baselinePsa = psaWarning;
    }

    // Validate positive cores <= total cores
    if (data.totalCores && data.positiveCores) {
      if (data.positiveCores > data.totalCores) {
        customErrors.positiveCores = {
          type: 'custom',
          message: 'Positive Cores cannot be greater than Total Cores',
        };
      }
    }

    const result = {
      values: data,
      errors: customErrors,
      warnings: warnings,
    } as ResolverResult<LdtProstateBiopsyReviseReportFormData> & {
      warnings: Partial<
        Record<keyof LdtProstateBiopsyReviseReportFormData, string>
      >;
    };

    if (warningsRef) {
      warningsRef.current = result.warnings;
    }

    return result;
  };

// Helper function to compute NCCN warning for all users (CS/Sales and non-CS)
const computeNccnWarning = (
  nccnRiskCategory?: string,
  primaryGleasonScore?: string,
  secondaryGleasonScore?: string,
  baselinePsa?: number,
  tStage?: string,
): string => {
  if (!nccnRiskCategory) return '';

  const validation = NccnRiskCategoryUtils.validateNccnRiskRules(
    nccnRiskCategory as NccnRiskCategory,
    primaryGleasonScore,
    secondaryGleasonScore,
    baselinePsa,
    tStage as TStage,
  );

  return validation.isValid
    ? ''
    : validation.errorMessage || 'NCCN Risk Category validation failed';
};

// Helper function to compute PSA warning
const computePsaWarning = (psaValue?: number): string => {
  if (!psaValue || psaValue <= 100) return '';
  return 'PSA exceeds 100.00 ng/mL. Please verify that the PSA is correct before proceeding.';
};

// Helper function to create default form values from Order
const createDefaultFormValues = (
  order: LdtOrderForReviseReportFieldsFragment,
): LdtProstateBiopsyReviseReportFormData => {
  return {
    firstName: order?.patient?.firstNamePhi?.trim() ?? '',
    lastName: order?.patient?.lastNamePhi?.trim() ?? '',
    dateOfBirth: order?.patient?.dateOfBirthPhi ?? '',
    ethnicity: (order?.patient?.ethnicity ?? '') as Ethnicity,
    orderingProviderId: order?.orderingProvider?.id ?? '',
    primaryGleasonScore: (order?.primaryGleasonScore?.toString() ?? '') as
      | '3'
      | '4'
      | '5',
    secondaryGleasonScore: (order?.secondaryGleasonScore?.toString() ?? '') as
      | '3'
      | '4'
      | '5',
    baselinePsa: order?.psa ?? null,
    tStage: (order?.tStage ?? '') as TStage,
    totalCores: order?.totalCores ?? null,
    positiveCores: order?.positiveCores ?? null,
    nccnRiskCategory: order.nccnRiskCategory ?? '',
    dateOfBiopsy: order?.labProcedureDatePhi ?? '',
    specimenId: order?.specimenId?.trim() ?? '',
    revisionReason: '',
  };
};

export const LdtProstateBiopsyReviseReportForm: FC<{
  order: LdtOrderForReviseReportFieldsFragment;
  ldtOrderRepository: LdtOrderRepository;
}> = ({ order, ldtOrderRepository }) => {
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const location = useLocation();

  // Ref to store warnings computed in the resolver
  const warningsRef = useRef<
    Partial<Record<keyof LdtProstateBiopsyReviseReportFormData, string>>
  >({});

  const defaultValues = createDefaultFormValues(order);

  const {
    control,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm<LdtProstateBiopsyReviseReportFormData>({
    resolver: createCustomResolver(warningsRef),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const {
    selectedOrderingProvider,
    hasOrderingProviderChanges,
    orderingProviderSectionProps,
    orderingProviderDialogProps,
  } = useOrderingProviderFormSection({
    ldtOrderRepository,
    isOrderingPhysicianUser: false,
    initialOrderingProvider: order.orderingProvider ?? null,
    setOrderingProviderId: (id, options) =>
      setValue('orderingProviderId', id, options),
    triggerOrderingProviderValidation: () => trigger('orderingProviderId'),
  });

  const currentValues = watch();
  const nccnRiskCategory = currentValues.nccnRiskCategory;

  // Run validation once on mount so resolver populates warnings and they display immediately when the form opens.
  // Exclude revision reason so it is not validated until the user interacts with it.
  useEffect(() => {
    const fieldsToValidate = (
      Object.keys(
        defaultValues,
      ) as (keyof LdtProstateBiopsyReviseReportFormData)[]
    ).filter((name) => !FIELDS_EXCLUDED_FROM_REVISE_ONE_CHECK.has(name));
    void trigger(fieldsToValidate);
    // defaultValues is stable for the lifetime of the form (derived from order on mount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  // Handler for calculating NCCN risk category
  const handleNccnRiskCalculation = useCallback(async () => {
    try {
      const formValues = getValues();
      const primaryGleason = parseInt(formValues.primaryGleasonScore ?? '', 10);
      const secondaryGleason = parseInt(
        formValues.secondaryGleasonScore ?? '',
        10,
      );
      const baselinePsa = formValues.baselinePsa ?? undefined;
      const tStage = formValues.tStage as TStage;
      const totalCoresValue = formValues.totalCores ?? undefined;
      const positiveCoresValue = formValues.positiveCores ?? undefined;

      const categories = NccnRiskCategoryUtils.categorizeNccnRisk({
        primaryGleason,
        secondaryGleason,
        baselinePsa: baselinePsa!,
        tStage,
        positiveCores: positiveCoresValue,
        totalCores: totalCoresValue,
      });

      // If multiple categories are possible (e.g., Low/VeryLow),
      // take the first one
      const calculatedCategory = categories[0];
      setValue('nccnRiskCategory', calculatedCategory, {
        shouldValidate: true,
      });
    } catch (error) {
      Sentry.captureException(error);
      notifyError(
        'Unable to calculate NCCN risk category with the provided values.',
      );
    }
  }, [getValues, setValue]);

  // Debounced validation triggers for performance optimization
  const debouncedNccnValidation = useDebounce(() => {
    trigger('nccnRiskCategory');
  }, 300);

  const debouncedPsaValidation = useDebounce(() => {
    trigger('baselinePsa');
  }, 300);

  const debouncedPositiveCoresValidation = useDebounce(() => {
    trigger('positiveCores');
  }, 300);

  // Button state - use state instead of ref so button can re-render when needed
  const [canCalculateNccn, setCanCalculateNccn] = useState(false);

  // Function to check if NCCN calculation is enabled
  // Only recalculates when called, not on every render
  const checkCanCalculateNccn = useCallback(() => {
    const formValues = getValues();
    const totalCoresValue = formValues.totalCores ?? null;
    const positiveCoresValue = formValues.positiveCores ?? null;

    const hasValidClinicalFields =
      !errors.primaryGleasonScore &&
      !errors.secondaryGleasonScore &&
      !errors.baselinePsa &&
      !errors.tStage &&
      !!formValues.primaryGleasonScore &&
      !!formValues.secondaryGleasonScore &&
      !!formValues.baselinePsa &&
      formValues.tStage;

    const hasValidCoreFields =
      !!totalCoresValue &&
      !!positiveCoresValue &&
      positiveCoresValue <= totalCoresValue &&
      !errors.totalCores &&
      !errors.positiveCores;

    const canCalculate = hasValidClinicalFields && hasValidCoreFields;
    setCanCalculateNccn(canCalculate);
    return canCalculate;
  }, [
    errors.primaryGleasonScore,
    errors.secondaryGleasonScore,
    errors.baselinePsa,
    errors.tStage,
    errors.totalCores,
    errors.positiveCores,
    getValues,
  ]);

  // Debounced version for onChange handlers to avoid lag
  const debouncedCheckCanCalculateNccn = useDebounce(() => {
    checkCanCalculateNccn();
  }, 150);

  // Update button state when errors change (validation runs)
  useEffect(() => {
    checkCanCalculateNccn();
  }, [checkCanCalculateNccn]);

  // Build ReviseReportInput for the API (required by loader / reviseReport mutation)
  const getReviseReportInputFromFormData =
    useCallback((): ReviseReportInput => {
      const formData = getValues();
      return {
        orderId: order.id,
        orderingProviderId: formData.orderingProviderId,
        patientDateOfBirthPhi: formData.dateOfBirth,
        patientEthnicity: (safeEmptyToUndefined(formData.ethnicity) ??
          null) as Ethnicity | null,
        patientFirstNamePhi: formData.firstName.trim(),
        patientLastNamePhi: formData.lastName.trim(),
        reason: formData.revisionReason.trim(),
        labProcedureDatePhi: formData.dateOfBiopsy,
        specimenId: formData.specimenId.trim(),
        prostateBiopsyData: {
          primaryGleasonScore: safeParseInt(formData.primaryGleasonScore)!,
          secondaryGleasonScore: safeParseInt(formData.secondaryGleasonScore)!,
          psa: formData.baselinePsa!,
          tStage: formData.tStage,
          nccnRiskCategory: formData.nccnRiskCategory as NccnRiskCategory,
          totalCores: formData.totalCores ?? null,
          positiveCores: formData.positiveCores ?? null,
        },
        metastaticProstateData: null,
        prostatePostRpData: null,
      };
    }, [getValues, order]);

  // Handle action results
  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state]);

  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [showReviseAtLeastOneError, setShowReviseAtLeastOneError] =
    useState(false);

  const hasRelevantDirtyFields =
    hasSemanticFormChanges(
      currentValues,
      defaultValues,
      FIELDS_EXCLUDED_FROM_REVISE_ONE_CHECK,
    ) || hasOrderingProviderChanges;

  useEffect(() => {
    if (hasRelevantDirtyFields && showReviseAtLeastOneError) {
      setShowReviseAtLeastOneError(false);
    }
  }, [hasRelevantDirtyFields, showReviseAtLeastOneError]);

  const onSubmitClick = async () => {
    setShowReviseAtLeastOneError(false);
    const isValid = await trigger();
    if (!isValid) return;
    if (!hasRelevantDirtyFields) {
      setShowReviseAtLeastOneError(true);
      return;
    }
    setSubmitDialogOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitDialogOpen(false);
    const input = getReviseReportInputFromFormData();
    const formData = new FormData();
    formData.append('action', OrderAction.REVISE_REPORT);
    formData.append('reviseReportInput', JSON.stringify(input));
    fetcher.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  };

  const handleCloseConfirmSubmitDialog = () => {
    setSubmitDialogOpen(false);
  };

  return (
    <Box
      component='form'
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmitClick();
      }}
      noValidate
      sx={{
        width: '100%',
        height: '100%',
        maxWidth: '2000px',
        maxHeight: '1500px',
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <LdtOrderFormHeader
        specimenType={SpecimenType.ProstateBiopsy}
        onClose={() => navigate(-1)}
        crossOrgId={order.crossOrgId}
        title='Revise Report'
      />
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
        }}
      >
        *Indicates a required field
      </Typography>
      {/* Scrollable content area */}
      <Box sx={{ flex: 1, overflow: 'auto', mt: 2 }}>
        {/* Physician Section */}
        <OrderingProviderFormSection
          control={control}
          errors={errors}
          trigger={trigger}
          ldtOrderRepository={ldtOrderRepository}
          selectedOrderingProvider={selectedOrderingProvider}
          {...orderingProviderSectionProps}
        />

        {/* Patient Information Section */}
        <PatientInformationFormSection control={control} errors={errors} />

        {/* Clinical & Pathology Information Section */}
        <FormSectionCard
          header={<SectionHeader title='Clinical & Pathology Information' />}
          sx={{ borderBottom: 'none' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Gleason Scores Row */}
            <GleasonScoreFields
              control={control}
              errors={errors}
              trigger={trigger}
            />

            {/* PSA and T-Stage Row */}
            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='baselinePsa'
                  control={control}
                  render={({ field }) => {
                    const psaValue = field.value;
                    const hasError = !!errors.baselinePsa;
                    // Get warning from resolver (computed in resolver)
                    const warningText = warningsRef.current.baselinePsa;

                    return (
                      <DecimalInput
                        required
                        fullWidth
                        label='Most Recent Pre-Biopsy PSA (ng/mL)'
                        value={psaValue}
                        decimalPlaces={2}
                        min={FORM_CONSTANTS.PSA_MIN}
                        error={hasError}
                        validationText={errors.baselinePsa?.message}
                        warningText={warningText}
                        onBlur={field.onBlur}
                        onChange={(value) => {
                          field.onChange(value);
                          // Debounced PSA validation - triggers after 300ms of no typing
                          debouncedPsaValidation();
                          // Debounced NCCN validation if NCCN category is set
                          if (value !== null && nccnRiskCategory) {
                            debouncedNccnValidation();
                          }
                          // Debounced button state update to avoid lag
                          debouncedCheckCanCalculateNccn();
                        }}
                      />
                    );
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='tStage'
                  control={control}
                  render={({ field }) => (
                    <SelectList
                      {...field}
                      required
                      label='Clinical T-stage'
                      labelId='t-stage-label'
                      options={getProstateBiopsyTStageOptions(true, false)}
                      validationText={errors.tStage?.message?.toString() ?? ''}
                      onChange={async (event) => {
                        field.onChange(event);
                        // Re-validate NCCN when dependent field changes
                        if (event.target.value && nccnRiskCategory) {
                          await trigger('nccnRiskCategory');
                        }
                        // Debounced button state update to avoid lag
                        debouncedCheckCanCalculateNccn();
                      }}
                    />
                  )}
                />
              </Box>
            </ResponsiveFormRow>

            {/* Total Cores and Positive Cores */}
            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='positiveCores'
                  control={control}
                  render={({ field }) => (
                    <IntegerInput
                      fullWidth
                      label='Positive Cores'
                      value={field.value ?? null}
                      min={1}
                      error={!!errors.positiveCores}
                      helperText={errors.positiveCores?.message || ' '}
                      onBlur={field.onBlur}
                      onChange={(value) => {
                        field.onChange(value);
                        // Debounced button state update to avoid lag
                        debouncedCheckCanCalculateNccn();
                        // Trigger validation to re-run custom resolver and clear error if valid
                        debouncedPositiveCoresValidation();
                      }}
                    />
                  )}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='totalCores'
                  control={control}
                  render={({ field }) => (
                    <IntegerInput
                      fullWidth
                      label='Total Cores'
                      value={field.value ?? null}
                      min={1}
                      error={!!errors.totalCores}
                      helperText={errors.totalCores?.message || ' '}
                      onBlur={field.onBlur}
                      onChange={(value) => {
                        field.onChange(value);
                        // Debounced button state update to avoid lag
                        debouncedCheckCanCalculateNccn();
                        // Re-validate positiveCores when totalCores changes
                        // (e.g., if positiveCores=3 and totalCores changes from 3 to 2, positiveCores becomes invalid)
                        debouncedPositiveCoresValidation();
                      }}
                    />
                  )}
                />
              </Box>
            </ResponsiveFormRow>

            {/* NCCN Risk Category */}
            <Controller
              name='nccnRiskCategory'
              control={control}
              render={({ field }) => {
                // Get warning from resolver (computed in resolver)
                const nccnWarning = warningsRef.current.nccnRiskCategory;
                const hasError = !!errors.nccnRiskCategory;

                return (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <FormLabel error={hasError} required>
                        NCCN Risk Category
                      </FormLabel>
                      <NccnInfoTooltip>
                        <IconButton
                          size='small'
                          aria-label='View NCCN Risk Category Guidelines'
                          sx={{ padding: '4px' }}
                        >
                          <img
                            src={InfoIcon}
                            alt='Info'
                            style={{ width: '16px', height: '16px' }}
                          />
                        </IconButton>
                      </NccnInfoTooltip>

                      <Button
                        size='small'
                        variant='contained'
                        onClick={handleNccnRiskCalculation}
                        disabled={!canCalculateNccn}
                        aria-label='Calculate NCCN Risk Category'
                      >
                        CALCULATE
                      </Button>
                    </Box>
                    <RadioGroupField
                      {...field}
                      required
                      row
                      name={field.name}
                      label='' // Label is now handled above
                      options={NCCN_RISK_CATEGORY_OPTIONS}
                      validationText={
                        errors.nccnRiskCategory?.message?.toString() ?? ''
                      }
                      warningText={nccnWarning || undefined}
                      onChange={async (event) => {
                        field.onChange(event);
                        if (event.target.value) {
                          await trigger('nccnRiskCategory');
                        }
                      }}
                    />
                  </Box>
                );
              }}
            />

            {/* Date of Biopsy & Specimen ID */}
            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='dateOfBiopsy'
                  control={control}
                  render={({ field }) => (
                    <FormDatePicker
                      label={SpecimenTypeUtils.getSpecimenTypeInputLabel(
                        SpecimenType.ProstateBiopsy,
                      )}
                      maxDate={new Date()}
                      value={field.value}
                      required
                      fullWidth
                      error={!!errors.dateOfBiopsy}
                      helperText={
                        errors.dateOfBiopsy?.message?.toString() ?? ' '
                      }
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='specimenId'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      label='Specimen ID'
                      error={!!errors.specimenId}
                      helperText={errors.specimenId?.message || ' '}
                    />
                  )}
                />
              </Box>
            </ResponsiveFormRow>
          </Box>
        </FormSectionCard>

        {/* Revision Reason - required, default always '' */}
        <FormSectionCard
          header={<SectionHeader title='Revision Reason' />}
          sx={{ borderBottom: 'none' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Controller
              name='revisionReason'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  multiline
                  rows={4}
                  placeholder='Type here...'
                  error={!!errors.revisionReason}
                  helperText={errors.revisionReason?.message || ' '}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 400 },
                    },
                  }}
                />
              )}
            />
          </Box>
        </FormSectionCard>
      </Box>
      {/* Fixed form actions at the bottom - single Submit button */}
      <Box
        sx={{
          flexShrink: 0,
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-end' },
          }}
        >
          <Button
            type='submit'
            variant='contained'
            color='primary'
            size='large'
            disabled={fetcher.state !== 'idle'}
            sx={{
              width: { xs: '100%', sm: '160px' },
              maxWidth: 350,
              minHeight: { xs: '48px', sm: 'auto' },
            }}
          >
            {fetcher.state !== 'idle' ? 'SUBMITTING...' : 'SUBMIT'}
          </Button>
          <Typography variant='body2' color='error' role='alert'>
            {showReviseAtLeastOneError
              ? "You must revise at least 1 field (core counts don't count)."
              : ''}
          </Typography>
        </Box>
      </Box>
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          title: 'Confirm Revise Report?',
          subtitle: 'I have reviewed and confirmed the information is correct.',
          type: FeedbackType.Primary,
          icon: Caution,
        }}
        isDialogOpen={submitDialogOpen}
        onPrimaryButtonClick={handleConfirmSubmit}
        onSecondaryButtonClick={handleCloseConfirmSubmitDialog}
        handleDialogClose={handleCloseConfirmSubmitDialog}
      />

      {orderingProviderDialogProps.open && (
        <OrderingProviderForm
          open
          orderRepository={ldtOrderRepository}
          onClose={orderingProviderDialogProps.onClose}
          initialProvider={orderingProviderDialogProps.initialProvider}
          onSuccess={orderingProviderDialogProps.onSuccess}
          onDelete={orderingProviderDialogProps.onDelete}
        />
      )}
    </Box>
  );
};
